import { ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../../../utils.ts';
import { ClickFormsExportRow } from '../../types.ts';
import { fillInBlankRows } from '../../utils.ts';
import { generate1025LikeAdjustmentRows } from './adjustments.ts';
import {
  FNMA1025_ADJUSTMENT_FIELDS,
  FNMA1025_FORM_CODE,
  FNMA1025_ROWS,
  FNMA1025_SALES_COMP_GRID_ROWS_PER_COMP,
  FNMA1025_TOTAL_ROWS,
} from './consts.ts';

export const generateFnma1025FormString = (
  compSequence: string[],
  reportJSON: ReportJSON,
  isMerge: boolean,
): string => {
  let rows: ClickFormsExportRow[] = [];
  const HEADER_STRING = isMerge
    ? `CMD.MERGE.${FNMA1025_FORM_CODE}.I1.BROADCAST\n`
    : `CMD.LOAD.${FNMA1025_FORM_CODE}\n`; // F340 is code for FNMA1025
  let result = HEADER_STRING;

  const subjectRows = generateSubjectRows(reportJSON.meta);
  rows = rows.concat(subjectRows);

  const { comps } = reportJSON;

  compSequence.forEach((compID, index) => {
    const comp = comps[compID];

    if (comp) {
      rows = rows.concat(
        generate1025LikeAdjustmentRows({
          comp: comp.salesGridData,
          compIndex: index,
          fieldMapping: FNMA1025_ADJUSTMENT_FIELDS,
          rowsPerColumn: FNMA1025_SALES_COMP_GRID_ROWS_PER_COMP,
        }),
      );
    }
  });

  // Cost Approach
  rows = rows.concat(generateCostApproachRows(reportJSON));

  result += fillInBlankRows(rows, FNMA1025_TOTAL_ROWS);
  return result;
};

const generateSubjectRows = (meta: ReportJSON['meta']): ClickFormsExportRow[] => {
  const rows: ClickFormsExportRow[] = [];

  rows.push({ row: FNMA1025_ROWS.SU_BLANK1_LABEL, value: 'Add. Features' });
  if (isDefined(meta?.otherFeature2Label)) {
    rows.push({ row: FNMA1025_ROWS.SU_BLANK2_LABEL, value: meta?.otherFeature2Label });
  }
  return rows;
};

const generateCostApproachRows = ({ subject: { costApproach } }: ReportJSON) => {
  const { remainingEcoLife, effectiveAge } = costApproach ?? {};
  const rows: ClickFormsExportRow[] = [];

  rows.push({
    row: FNMA1025_ROWS.CA_SUPPORT_FOR_SITE_VALUE,
    value: COST_APPROACH_SITE_COMMENT,
  });
  rows.push({ row: FNMA1025_ROWS.CA_REPLACEMENT_COST_NEW_CHECKBOX, value: 'X' });

  rows.push({
    row: FNMA1025_ROWS.CA_COMMENTS,
    value: getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_COST_DATA_SOURCE,
    value: costApproach?.source?.name,
  });
  rows.push({
    row: FNMA1025_ROWS.CA_QUALITY,
    value: costApproach?.dwellingCostQuality,
  });
  rows.push({
    row: FNMA1025_ROWS.CA_EFFECTIVE_DATE,
    value: costApproach?.source?.effectiveDate.toString(),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_ESTIMATED_REMAINING_ECON_LIFE,
    value: `${remainingEcoLife ?? ''}`,
  });

  rows.push({
    row: FNMA1025_ROWS.CA_EFFECTIVE_AGE,
    value: `${effectiveAge ?? ''}`,
  });

  rows.push({
    row: FNMA1025_ROWS.CA_OPINION_OF_SITE_VALUE,
    value: scalarToNumberToString(costApproach?.site),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_DWELLING_SQFT,
    value: scalarToNumberToString(costApproach?.dwelling?.sqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_DWELLING_COST_PER_SQFT,
    value: scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_DWELLING_COST,
    value: scalarToNumberToString(costApproach?.dwelling?.totalCost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_OTHER1_LABEL,
    value: costApproach?.basement ? 'Basement' : '',
  });
  rows.push({
    row: FNMA1025_ROWS.CA_OTHER1_SQFT,
    value: scalarToNumberToString(costApproach?.basement?.sqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_OTHER1_COST_PER_SQFT,
    value: scalarToNumberToString(costApproach?.basement?.costPerSqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_OTHER1_COST,
    value: scalarToNumberToString(costApproach?.basement?.totalCost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_OTHER2,
    value: costApproach?.additionalStructures ? 'Add. Structures' : '',
  });
  rows.push({
    row: FNMA1025_ROWS.CA_OTHER2_COST,
    value: scalarToNumberToString(costApproach?.additionalStructures?.totalCost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_GARAGE_CARPORT_SQFT,
    value: scalarToNumberToString(costApproach?.garageCarport?.sqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_GARAGE_CARPORT_COST_PER_SQFT,
    value: scalarToNumberToString(costApproach?.garageCarport?.costPerSqft),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_GARAGE_CARPORT_COST,
    value: scalarToNumberToString(costApproach?.garageCarport?.totalCost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_TOTAL_ESTIMATE_OF_COST_NEW,
    value: scalarToNumberToString(costApproach?.new?.cost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_DEPR_PHYS,
    value: scalarToNumberToString(costApproach?.depreciation?.physical),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_DEPR_FUNC,
    value: scalarToNumberToString(costApproach?.depreciation?.functional),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_DEPR_EXTN,
    value: scalarToNumberToString(costApproach?.depreciation?.external),
  });
  rows.push({
    row: FNMA1025_ROWS.CA_DEPR_TOTAL,
    value: scalarToNumberToString(costApproach?.depreciation?.total),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_DEPR_COST_OF_IMPROVEMENTS,
    value: scalarToNumberToString(costApproach?.depreciated?.cost),
  });

  rows.push({
    row: FNMA1025_ROWS.CA_INDICATED_VALUE,
    value: scalarToNumberToString(costApproach?.indicatedValue),
  });

  return rows;
};
